import { useEffect, useState } from "react";
import { getHomework, submitHomework, skipHomework, type HomeworkTask } from "../lib/api";

interface Props { onClose: () => void; }

const KIND_META: Record<HomeworkTask["kind"], { emoji: string; label: string; color: string }> = {
  write:   { emoji: "✏️",  label: "Write",       color: "border-sky-700/40 bg-sky-500/10" },
  vocab:   { emoji: "📚", label: "Vocab review", color: "border-fuchsia-700/40 bg-fuchsia-500/10" },
  reflect: { emoji: "💭", label: "Reflect",     color: "border-amber-700/40 bg-amber-500/10" },
  listen:  { emoji: "🎧", label: "Listen",      color: "border-emerald-700/40 bg-emerald-500/10" },
};

export function HomeworkView({ onClose }: Props): JSX.Element {
  const [tasks, setTasks] = useState<HomeworkTask[] | null>(null);
  const [drafts, setDrafts] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  function refresh(): void {
    void getHomework().then(setTasks);
  }
  useEffect(refresh, []);

  async function handleSubmit(t: HomeworkTask): Promise<void> {
    const txt = (drafts[t.id] ?? "").trim();
    if (!txt) return;
    setBusy(t.id);
    const ok = await submitHomework(t.id, txt);
    setBusy(null);
    if (ok) refresh();
  }
  async function handleSkip(t: HomeworkTask): Promise<void> {
    setBusy(t.id);
    await skipHomework(t.id);
    setBusy(null);
    refresh();
  }

  if (tasks === null) {
    return <div className="rounded-2xl border border-ink-700 bg-ink-900/60 p-8 text-center text-sm text-ink-400">Loading…</div>;
  }
  if (tasks.length === 0) {
    return (
      <div className="rounded-2xl border border-emerald-700/40 bg-emerald-500/5 p-6 text-center">
        <div className="text-3xl mb-2">{"✓"}</div>
        <div className="text-white font-medium">No homework right now.</div>
        <p className="text-sm text-ink-400 mt-1">Finish a lesson and Arthur will assign new tasks.</p>
        <button onClick={onClose} className="mt-4 rounded-lg bg-lime-500 hover:bg-lime-400 text-ink-900 font-medium px-4 py-2 text-sm">
          Back to dashboard
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {tasks.map((t) => {
        const meta = KIND_META[t.kind];
        return (
          <div key={t.id} className={`rounded-2xl border ${meta.color} p-5`}>
            <div className="flex items-center justify-between">
              <div className="text-[10px] uppercase tracking-widest text-ink-300">
                <span className="mr-1.5">{meta.emoji}</span>{meta.label}
              </div>
              {t.due_at && (
                <div className="text-[10px] text-ink-400">
                  due {new Date(t.due_at).toLocaleDateString(undefined, { day: "numeric", month: "short" })}
                </div>
              )}
            </div>
            <div className="text-white font-medium mt-1">{t.prompt}</div>
            {t.hint && <div className="text-xs text-ink-300 mt-1 italic">{t.hint}</div>}

            {t.kind !== "vocab" && (
              <textarea
                value={drafts[t.id] ?? ""}
                onChange={(e) => setDrafts({ ...drafts, [t.id]: e.target.value })}
                rows={3}
                placeholder="Your response…"
                className="mt-3 w-full rounded-xl bg-ink-900/60 border border-ink-700 px-3 py-2 text-sm text-white placeholder-ink-500 focus:outline-none focus:border-lime-500"
              />
            )}

            <div className="flex items-center gap-2 mt-3">
              {t.kind === "vocab" ? (
                <button
                  onClick={() => handleSkip(t)}
                  disabled={busy === t.id}
                  className="text-xs text-lime-200 underline-offset-2 hover:underline"
                >
                  Mark done (review in the Vocab tab)
                </button>
              ) : (
                <button
                  onClick={() => handleSubmit(t)}
                  disabled={busy === t.id || !(drafts[t.id] ?? "").trim()}
                  className="rounded-lg bg-lime-500 hover:bg-lime-400 text-ink-900 font-medium px-3 py-1.5 text-xs disabled:opacity-40"
                >
                  Submit
                </button>
              )}
              <button
                onClick={() => handleSkip(t)}
                disabled={busy === t.id}
                className="text-xs text-ink-400 hover:text-ink-200 underline-offset-2 hover:underline"
              >
                Skip
              </button>
            </div>
          </div>
        );
      })}
      <div className="text-center pt-2">
        <button onClick={onClose} className="text-xs text-ink-400 hover:text-white">← Back to dashboard</button>
      </div>
    </div>
  );
}
